import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../core/services/api.service';

export interface UserModel {
    userId?: number;
    firstName: string;
    lastName: string;
    email: string;
    entraUserId?: string;
    roleId: number;
    roleName: string;
    userTypeId?: number;
    userTypeName?: string;
    isActive: boolean;
    isAccept?: boolean;
    colorCode?: string;
    createdAt?: string;
    updatedAt?: string;
    createdBy?: number;
    updatedBy?: number;
    password?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    phoneNumber?: string;
    photoPath?: string;
}

export interface ProjectUserModel extends UserModel {
    projectId: number;
    onField?: boolean;
}

export interface UserResponse {
    success: boolean;
    message: string;
    user?: UserModel;
    users?: UserModel[];
}

export interface RoleModel {
    roleId: number;
    roleName: string;
}

export interface UserTypeModel {
    userTypeId: number;
    name: string;
    description?: string;
    isActive: boolean;
}

export interface RoleResponse {
    success: boolean;
    data: RoleModel[];
}

export interface UserTypeResponse {
    success: boolean;
    data: UserTypeModel[];
}

export interface InviteUserRequest {
    email: string;
    firstName: string;
    lastName: string;
    roleId: number;
    userTypeId: number;
    projectId: number;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zip?: string;
    country?: string;
    phoneNumber: string;
}

@Injectable({
    providedIn: 'root'
})
export class UserService {
    constructor(private apiService: ApiService) { }

    getUserList(): Observable<UserModel[]> {
        return this.apiService.get(`User/GetUserList`);
    }

    getUserById(userId: number): Observable<UserModel> {
        return this.apiService.get(`User/GetUserById/${userId}`);
    }

    getCurrentUser(): Observable<UserModel> {
        return this.apiService.get('User/GetCurrentUser');
    }

    getProjectUserList(projectId: number): Observable<ProjectUserModel[]> {
        return this.apiService.get(`User/GetProjectUserList/${projectId}`);
    }

    getRoles(): Observable<RoleModel[]> {
        return this.apiService.get('User/Roles').pipe(
            map((response: any) => response.data || [])
        );
    }

    getUserTypes(): Observable<UserTypeModel[]> {
        return this.apiService.get('User/Types').pipe(
            map((response: any) => response.data || [])
        );
    }

    inviteUser(inviteRequest: InviteUserRequest): Observable<UserResponse> {
        return this.apiService.post('User/Invite', inviteRequest);
    }

    editUser(userId: number, user: Partial<UserModel>): Observable<UserResponse> {
        return this.apiService.put(`User/${userId}`, user);
    }

    updateAddress(userId: number, address: { address1?: string | null; address2?: string | null; city: string; state: string; zip?: string | null; country?: string | null }): Observable<UserResponse> {
        return this.apiService.put(`User/${userId}/address`, address);
    }

    uploadProfilePicture(userId: number, file: File): Observable<{ success: boolean; photoPath: string }> {
        const formData = new FormData();
        formData.append('file', file);
        return this.apiService.post(`User/${userId}/profile-picture`, formData);
    }

    toggleUserStatus(userId: number, isActive: boolean): Observable<UserResponse> {
        return this.apiService.put(`User/${userId}/status`, { isActive });
    }

    getRoleList(): Observable<RoleModel[]> {
        return this.apiService.get('User/GetRoleList');
    }

    getUserByEmail(email: string): Observable<UserModel> {
        return this.apiService.get(`User/GetUserByEmail/${encodeURIComponent(email)}`);
    }

    validateColorCode(colorCode: string): Observable<{ success: boolean; isAvailable: boolean; message: string; usedByUser?: any }> {
        return this.apiService.get(`User/ValidateColorCode?colorCode=${encodeURIComponent(colorCode)}`);
    }

}
